import { useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Textarea } from "@/components/ui/textarea";
import { ResponsiveTable } from "@/shared/components/ResponsiveTable";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useNotification } from "@/shared/hooks/useNotification";
import { randomId } from "@/shared/utils/randomId";
import { useActiveCropTypes } from "@/features/production/hooks/useCropTypes";
import { useCropVarietiesByCropType } from "@/features/production/hooks/useCropVarieties";
import { useCreateAgroforestryReceipt } from "../hooks/useAgroforestry";
import {
  PROVENANCE,
  type AgroforestryReceiptProvenance,
} from "../services/agroforestryApi";

type DraftLine = {
  tempId: string;
  cropTypeId: string;
  cropTypeSearch: string;
  cropVarietyId: string;
  cropVarietySearch: string;
  quantity: string;
  unitCost: string;
};

const newLine = (): DraftLine => ({
  tempId: randomId(),
  cropTypeId: "",
  cropTypeSearch: "",
  cropVarietyId: "",
  cropVarietySearch: "",
  quantity: "",
  unitCost: "",
});

const todayIso = () => new Date().toISOString().slice(0, 10);

export const AgroforestryReceiveStockPage = () => {
  const notify = useNotification();
  const cropTypesQuery = useActiveCropTypes();
  const createMutation = useCreateAgroforestryReceipt();

  const [provenance, setProvenance] = useState<AgroforestryReceiptProvenance>(
    PROVENANCE.ExternalPurchase,
  );
  const [receivedDate, setReceivedDate] = useState<string>(todayIso());
  const [supplierName, setSupplierName] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);

  const cropTypes = (cropTypesQuery.data ?? []).filter((ct) => ct.isAgroforestry);
  const isInHouse = provenance === PROVENANCE.InHousePropagation;

  const isSubmittable = useMemo(() => {
    if (!receivedDate) return false;
    if (lines.length === 0) return false;
    if (!isInHouse && !supplierName.trim()) return false; // external requires supplier
    return lines.every(
      (l) =>
        l.cropTypeId &&
        l.cropVarietyId &&
        Number(l.quantity) > 0 &&
        // UnitCost only matters for external purchases; in-house ignores it entirely.
        (isInHouse || l.unitCost === "" || Number(l.unitCost) > 0),
    );
  }, [receivedDate, lines, isInHouse, supplierName]);

  const handleSubmit = () => {
    if (!isSubmittable) {
      notify.error(
        isInHouse
          ? "Fill in every line — crop, variety, and a positive quantity."
          : "Fill in supplier name and every line.",
      );
      return;
    }
    createMutation.mutate(
      {
        provenance,
        receivedDate: new Date(receivedDate).toISOString(),
        supplierName: isInHouse ? undefined : supplierName.trim() || undefined,
        paymentReference: isInHouse
          ? undefined
          : paymentReference.trim() || undefined,
        notes: notes.trim() || undefined,
        lines: lines.map((l) => ({
          cropTypeId: l.cropTypeId,
          cropVarietyId: l.cropVarietyId,
          quantity: Number(l.quantity),
          // In-house propagation has no purchase price — drop unit cost even if the
          // user typed one before switching tabs.
          unitCost: isInHouse || l.unitCost === "" ? undefined : Number(l.unitCost),
        })),
      },
      {
        onSuccess: (receipt) => {
          notify.success(`Receipt ${receipt.receiptNumber} submitted — awaiting GM approval.`);
          setSupplierName("");
          setPaymentReference("");
          setNotes("");
          setLines([newLine()]);
        },
        onError: () => notify.error("Failed to submit receipt."),
      },
    );
  };

  const updateLine = (id: string, patch: Partial<DraftLine>) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l.tempId !== id) return l;
        const next: DraftLine = { ...l, ...patch };
        // Clear variety + its search when crop type changes — varieties are filtered.
        if (patch.cropTypeId !== undefined && patch.cropTypeId !== l.cropTypeId) {
          next.cropVarietyId = "";
          next.cropVarietySearch = "";
        }
        return next;
      }),
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Receive Agroforestry Stock</h1>
        <p className="mt-1 text-muted-foreground">
          Record tree seedlings entering the agroforestry catalogue — externally purchased
          (cash, cheque, or M-Pesa) or propagated in-house. The General Manager approves
          before stock becomes available; the agroforestry team marks each batch ready
          once it has healed.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Source</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs
            value={provenance === PROVENANCE.InHousePropagation ? "in-house" : "external"}
            onValueChange={(v) =>
              setProvenance(
                v === "in-house"
                  ? PROVENANCE.InHousePropagation
                  : PROVENANCE.ExternalPurchase,
              )
            }
          >
            <TabsList>
              <TabsTrigger value="external">External purchase</TabsTrigger>
              <TabsTrigger value="in-house">In-house propagation</TabsTrigger>
            </TabsList>
          </Tabs>
          <p className="mt-2 text-sm text-muted-foreground">
            {isInHouse
              ? "Tree seedlings propagated on the farm. No supplier or payment reference is captured; the receipt still goes through GM approval as a single audit trail."
              : "Tree seedlings bought from a third party. Supplier name is required; payment reference is optional but recommended for traceability."}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Receipt details</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="receivedDate">Received date</FieldLabel>
            <DatePicker
              id="receivedDate"
              value={receivedDate}
              onChange={setReceivedDate}
              max={todayIso()}
              placeholder="Pick a date"
            />
          </Field>
          {!isInHouse && (
            <>
              <Field>
                <FieldLabel htmlFor="supplierName">
                  Supplier (free text) <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="supplierName"
                  placeholder='e.g. "John (Vihiga)"'
                  value={supplierName}
                  onChange={(e) => setSupplierName(e.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="paymentReference">Payment reference</FieldLabel>
                <Input
                  id="paymentReference"
                  placeholder="Cheque #, M-Pesa code, etc."
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                />
              </Field>
            </>
          )}
          <Field className={isInHouse ? "md:col-span-2" : "md:col-span-2"}>
            <FieldLabel htmlFor="notes">Notes</FieldLabel>
            <Textarea
              id="notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                isInHouse
                  ? "Optional context — e.g. propagation batch reference, location."
                  : undefined
              }
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>Lines</CardTitle>
            <Button variant="outline" onClick={() => setLines((prev) => [...prev, newLine()])}>
              + Add line
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveTable
            className="rounded-lg border"
            mobileFallback={
              <div className="space-y-3">
                {lines.map((line, index) => (
                  <ReceiptLineCard
                    key={line.tempId}
                    index={index}
                    line={line}
                    cropTypes={cropTypes}
                    showUnitCost={!isInHouse}
                    onChange={(patch) => updateLine(line.tempId, patch)}
                    onRemove={
                      lines.length > 1
                        ? () => setLines((prev) => prev.filter((l) => l.tempId !== line.tempId))
                        : undefined
                    }
                  />
                ))}
              </div>
            }
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Crop type</TableHead>
                  <TableHead>Variety</TableHead>
                  <TableHead>Quantity (pieces)</TableHead>
                  {!isInHouse && <TableHead>Unit cost (KES, optional)</TableHead>}
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line) => (
                  <ReceiptLineRow
                    key={line.tempId}
                    line={line}
                    cropTypes={cropTypes}
                    showUnitCost={!isInHouse}
                    onChange={(patch) => updateLine(line.tempId, patch)}
                    onRemove={
                      lines.length > 1
                        ? () => setLines((prev) => prev.filter((l) => l.tempId !== line.tempId))
                        : undefined
                    }
                  />
                ))}
              </TableBody>
            </Table>
          </ResponsiveTable>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSubmit}
          disabled={!isSubmittable || createMutation.isPending}
        >
          {createMutation.isPending ? "Submitting..." : "Submit for approval"}
        </Button>
      </div>
    </div>
  );
};

type Row = {
  line: DraftLine;
  cropTypes: { id: string; name: string }[];
  showUnitCost: boolean;
  onChange: (patch: Partial<DraftLine>) => void;
  onRemove?: () => void;
};

const ReceiptLineRow = ({ line, cropTypes, showUnitCost, onChange, onRemove }: Row) => {
  return (
    <TableRow>
      <TableCell className="min-w-[12rem]">
        <CropTypeCombobox line={line} cropTypes={cropTypes} onChange={onChange} />
      </TableCell>
      <TableCell className="min-w-[12rem]">
        <CropVarietyCombobox line={line} cropTypes={cropTypes} onChange={onChange} />
      </TableCell>
      <TableCell>
        <QuantityInput value={line.quantity} onChange={(quantity) => onChange({ quantity })} />
      </TableCell>
      {showUnitCost && (
        <TableCell>
          <UnitCostInput value={line.unitCost} onChange={(unitCost) => onChange({ unitCost })} />
        </TableCell>
      )}
      <TableCell className="text-right">
        {onRemove ? (
          <Button
            size="icon"
            variant="ghost"
            onClick={onRemove}
            aria-label="Remove line"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
};

/** Stacked one-field-per-row line editor for phones — same state, no sideways scroll. */
const ReceiptLineCard = ({
  index,
  line,
  cropTypes,
  showUnitCost,
  onChange,
  onRemove,
}: Row & { index: number }) => {
  return (
    <div className="rounded-lg border p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Line {index + 1}</span>
        {onRemove ? (
          <Button
            size="icon"
            variant="ghost"
            onClick={onRemove}
            aria-label={`Remove line ${index + 1}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
      <Field>
        <FieldLabel>Crop type</FieldLabel>
        <CropTypeCombobox line={line} cropTypes={cropTypes} onChange={onChange} />
      </Field>
      <Field>
        <FieldLabel>Variety</FieldLabel>
        <CropVarietyCombobox line={line} cropTypes={cropTypes} onChange={onChange} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field>
          <FieldLabel>Quantity (pcs)</FieldLabel>
          <QuantityInput value={line.quantity} onChange={(quantity) => onChange({ quantity })} />
        </Field>
        {showUnitCost && (
          <Field>
            <FieldLabel>Unit cost (KES)</FieldLabel>
            <UnitCostInput value={line.unitCost} onChange={(unitCost) => onChange({ unitCost })} />
          </Field>
        )}
      </div>
    </div>
  );
};

const CropTypeCombobox = ({
  line,
  cropTypes,
  onChange,
}: Pick<Row, "line" | "cropTypes" | "onChange">) => {
  // Client-side filter: the lists are typically small enough that this is fine.
  const cropQuery = line.cropTypeSearch.toLowerCase().trim();
  const filteredCropTypes = cropQuery
    ? cropTypes.filter((c) => c.name.toLowerCase().includes(cropQuery))
    : cropTypes;

  return (
    <Combobox
      value={line.cropTypeId || null}
      onValueChange={(v) => {
        const id = (v as string) ?? "";
        onChange({
          cropTypeId: id,
          cropTypeSearch: id ? cropTypes.find((c) => c.id === id)?.name ?? "" : "",
        });
      }}
      inputValue={line.cropTypeSearch}
    >
      <ComboboxInput
        placeholder="Search crop..."
        showClear={!!line.cropTypeId}
        className="w-full"
        onChange={(e) => onChange({ cropTypeSearch: e.target.value })}
      />
      <ComboboxContent>
        <ComboboxList>
          {filteredCropTypes.length === 0 && (
            <ComboboxEmpty>No crops found.</ComboboxEmpty>
          )}
          {filteredCropTypes.map((c) => (
            <ComboboxItem key={c.id} value={c.id}>
              {c.name}
            </ComboboxItem>
          ))}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

const CropVarietyCombobox = ({
  line,
  cropTypes,
  onChange,
}: Pick<Row, "line" | "cropTypes" | "onChange">) => {
  const varietiesQuery = useCropVarietiesByCropType(line.cropTypeId);
  const varieties = varietiesQuery.data ?? [];

  const varietyQuery = line.cropVarietySearch.toLowerCase().trim();
  const filteredVarieties = varietyQuery
    ? varieties.filter((v) => v.name.toLowerCase().includes(varietyQuery))
    : varieties;

  return (
    <Combobox
      value={line.cropVarietyId || null}
      onValueChange={(v) => {
        const id = (v as string) ?? "";
        onChange({
          cropVarietyId: id,
          cropVarietySearch: id ? varieties.find((cv) => cv.id === id)?.name ?? "" : "",
        });
      }}
      inputValue={line.cropVarietySearch}
      disabled={!line.cropTypeId || varieties.length === 0}
    >
      <ComboboxInput
        placeholder={line.cropTypeId ? "Search variety..." : "Pick crop first"}
        showClear={!!line.cropVarietyId}
        className="w-full"
        onChange={(e) => onChange({ cropVarietySearch: e.target.value })}
      />
      <ComboboxContent>
        <ComboboxList>
          {filteredVarieties.length === 0 && (
            <ComboboxEmpty>No varieties found.</ComboboxEmpty>
          )}
          {filteredVarieties.map((v) => (
            <ComboboxItem key={v.id} value={v.id}>
              {v.name}
            </ComboboxItem>
          ))}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

const QuantityInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => (
  <Input
    type="number"
    inputMode="decimal"
    min="0"
    step="0.01"
    value={value}
    onChange={(e) => onChange(e.target.value)}
  />
);

const UnitCostInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => (
  <Input
    type="number"
    inputMode="decimal"
    min="0.01"
    step="0.01"
    value={value}
    onChange={(e) => {
      const val = e.target.value.replace(/[^0-9.]/g, "");
      onChange(val);
    }}
    onKeyDown={(e) => {
      if (e.key === "-" || e.key === "e" || e.key === "E" || e.key === "+") {
        e.preventDefault();
      }
    }}
  />
);

export default AgroforestryReceiveStockPage;
